import { defaultFlags, FlagKeys, FlagsObject } from 'constants/flags'
import useUserProfile from 'modules/user/useUserProfile'
import { useMemo } from 'react'

import { useDevToolsSetting } from './settings'

const validFlagKeys = Object.keys(defaultFlags)

interface UseDevToolsFlags {
  backendFlags: FlagsObject
  localFlagOverrides: Partial<FlagsObject>
  setLocalFlagOverride: (flag: FlagKeys, value: boolean) => void
  clearLocalFlagOverride: (flag: FlagKeys) => void
  isOverridden: boolean
}

const formatArrayIntoObject = (flags: string[]) =>
  flags.reduce((acc, value) => ({ ...acc, [value]: true }), {})

const useBackendFlags = () => {
  const { data } = useUserProfile()

  const flagsKey = data?.me?.featureFlags?.join(',')
  // flagsData is an array and it is used as a dependency
  // to other React Hooks. However, it is possible that the
  // to this array will change between rerenders, which cause
  // React Hooks to rerun and particularly `useEffect` causes
  // infinite loop. Here we are making that reference to flagsData
  // would change only when underlying data is changed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const flagsData = useMemo(() => data?.me?.featureFlags, [flagsKey])

  const backendFlags = useMemo((): FlagsObject => {
    const featureFlags = flagsData?.map(f => f as string) ?? []

    return {
      ...defaultFlags,
      ...formatArrayIntoObject(featureFlags),
    }
  }, [flagsData])

  return backendFlags
}

const getOverrides = (
  baseFlags: Partial<FlagsObject>,
  backendFlags: FlagsObject
) => {
  const overrides = Object.fromEntries(
    (Object.entries(baseFlags) as [FlagKeys, boolean][]).filter(
      ([flagKey, value]) => {
        if (!validFlagKeys.includes(flagKey)) return false

        const backendValue = backendFlags[flagKey] ?? false
        return backendValue !== value
      }
    )
  ) as Partial<FlagsObject>

  return overrides
}

export const useDevToolsFlags = (): UseDevToolsFlags => {
  const [storedFlags, setStoredFlags] = useDevToolsSetting(
    'featureFlags.overrides'
  )
  const [isDevToolsTurnedOn] = useDevToolsSetting('general.isTurnedOn')

  const backendFlags = useBackendFlags()

  if (!isDevToolsTurnedOn) {
    return {
      backendFlags,
      clearLocalFlagOverride: () => {},
      isOverridden: false,
      localFlagOverrides: {},
      setLocalFlagOverride: () => {},
    }
  }

  const localFlagOverrides = getOverrides(storedFlags, backendFlags)

  const setLocalFlagOverride = (flag: FlagKeys, value: boolean) => {
    const newOverrides = getOverrides(
      { ...storedFlags, [flag]: value },
      backendFlags
    )
    setStoredFlags(newOverrides)
  }

  const clearLocalFlagOverride = (flag: FlagKeys) => {
    const newOverrides = Object.fromEntries(
      (Object.entries(localFlagOverrides) as [FlagKeys, boolean][]).filter(
        ([flagKey]) => flagKey !== flag
      )
    ) as Partial<FlagsObject>

    setStoredFlags(newOverrides)
  }

  const isOverridden = Object.keys(localFlagOverrides).length > 0

  return {
    localFlagOverrides: localFlagOverrides as any,
    setLocalFlagOverride,
    isOverridden,
    backendFlags,
    clearLocalFlagOverride,
  }
}
